import {
  can,
  ESTIMATE_STATUSES,
  ESTIMATE_VERSION_KINDS,
  type EstimateChangeImpact,
  type EstimateImportResult,
  type EstimateSummary,
  type EstimateVersionKind,
  type ProjectSummary,
  seesMoney,
} from '@epc/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import clsx from 'clsx';
import { FileUp, ListTree } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useCrumbs } from '../../layout/crumbs';
import { api } from '../../lib/api';
import { tenge, tengeShort } from '../../lib/format';
import { budgetQuery, projectsQuery } from '../../lib/projects';
import { useMe } from '../../lib/session';
import { Button } from '../../ui/button';
import { Dialog } from '../../ui/dialog';
import { Badge, EmptyState, ErrorBox, Spinner, toast } from '../../ui/feedback';
import { Checkbox, Field, Input, Select } from '../../ui/form';
import { Page, PageHeader, Panel, TitleBlock } from '../../ui/layout';
import { estimatesQuery, estimatesSummaryQuery, hours } from './lib';

const STATUS_TONE: Record<EstimateSummary['status'], 'neutral' | 'info' | 'success' | 'warning'> = {
  draft: 'neutral',
  review: 'info',
  approved: 'success',
  archived: 'warning',
};

export function StatusBadge({ status }: { status: EstimateSummary['status'] }) {
  return <Badge tone={STATUS_TONE[status]}>{ESTIMATE_STATUSES[status]}</Badge>;
}

/** Диалог загрузки сметы из KENML 1.0: проект, объект, вид версии */
export function ImportDialog({
  project,
  rootId,
  rootCode,
  onClose,
  onDone,
}: {
  project: ProjectSummary;
  /** Новая версия существующей сметы */
  rootId?: string;
  rootCode?: string;
  onClose: () => void;
  onDone: (r: EstimateImportResult) => void;
}) {
  const ref = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [objectId, setObjectId] = useState('');
  const [budgetLineId, setBudgetLineId] = useState('');
  const [versionKind, setVersionKind] = useState<EstimateVersionKind>(rootId ? 'expertise' : 'design');
  // Причина новой версии и запрос на изменение, по которому она сделана (СМ-12)
  const [reason, setReason] = useState('');
  const [changeId, setChangeId] = useState('');
  const linked = useQuery({
    queryKey: ['estimate', rootId, 'change-impact'],
    queryFn: () => api<EstimateChangeImpact>(`/estimates/${rootId}/change-impact`),
    enabled: !!rootId,
    retry: false,
  });
  // Разделы бюджета видны только с уровнем «все деньги»: без него поле не показывается
  const me = useMe();
  const canBudget =
    seesMoney(me.access) &&
    can(me.access, 'budget.view', {
      legalEntityId: project.legalEntityId,
      directionId: project.directionId,
      projectId: project.id,
      projectPath: [project.parentId],
    });
  const budget = useQuery({ ...budgetQuery(project.id), enabled: canBudget && !rootId });
  const sections = (budget.data?.lines ?? []).filter((l) => l.kind === 'section');
  const m = useMutation({
    mutationFn: () => {
      const form = new FormData();
      form.append('projectId', project.id);
      if (objectId) form.append('objectId', objectId);
      if (budgetLineId) form.append('budgetLineId', budgetLineId);
      if (rootId) form.append('rootId', rootId);
      form.append('versionKind', versionKind);
      if (rootId && reason.trim()) form.append('reason', reason.trim());
      if (rootId && changeId) form.append('changeId', changeId);
      form.append('file', file!);
      return api<EstimateImportResult>('/estimates/import', { form });
    },
    onSuccess: onDone,
  });
  return (
    <Dialog
      open
      onOpenChange={(v) => !v && onClose()}
      title={rootId ? `Новая версия сметы ${rootCode ?? ''}` : 'Загрузить смету из файла'}
      description="KENML 1.0 (.kenml) из АВС-KZ, Сметы РК или SANA — портал пересчитает все позиции по ресурсам и покажет расхождения с файлом. Excel по форме 4* (.xls, .xlsx) из АВС — позиции берутся как аналоги по стоимости единицы; точнее — KENML."
      footer={
        <>
          <Button className="min-h-[44px] md:min-h-0" variant="ghost" onClick={onClose}>
            Отмена
          </Button>
          <Button
            className="min-h-[44px] md:min-h-0"
            variant="primary"
            loading={m.isPending}
            disabled={!file}
            onClick={() => m.mutate()}
          >
            Загрузить
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <Field label="Файл сметы">
          <input
            ref={ref}
            type="file"
            accept=".kenml,.xml,.xls,.xlsx,text/xml,application/xml,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            className="hidden"
            onChange={(e) => setFile(e.target.files?.[0] ?? null)}
          />
          <div className="flex flex-wrap items-center gap-3">
            <Button
              className="min-h-[44px] md:min-h-0"
              variant="secondary"
              icon={<FileUp className="size-4" />}
              onClick={() => ref.current?.click()}
            >
              Выбрать файл
            </Button>
            <span className="text-[13px] text-ink-2 [overflow-wrap:anywhere]">
              {file ? file.name : 'Файл не выбран'}
            </span>
          </div>
        </Field>
        {!rootId ? (
          <Field label="Объект проекта" hint="Можно указать позже в карточке сметы">
            <Select value={objectId} onChange={(e) => setObjectId(e.target.value)}>
              <option value="">— по титулу сметы —</option>
              {project.objects.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.code ? `${o.code} — ` : ''}
                  {o.name}
                </option>
              ))}
            </Select>
          </Field>
        ) : null}
        {!rootId && sections.length ? (
          <Field label="Раздел бюджета" hint="Себестоимость раздела считается от привязанных смет (БМ-01)">
            <Select value={budgetLineId} onChange={(e) => setBudgetLineId(e.target.value)}>
              <option value="">— указать позже в карточке сметы —</option>
              {sections.map((l) => (
                <option key={l.id} value={l.id}>
                  {l.code ? `${l.code}. ` : ''}
                  {l.name}
                </option>
              ))}
            </Select>
          </Field>
        ) : null}
        <Field label="Вид версии">
          <Select value={versionKind} onChange={(e) => setVersionKind(e.target.value as EstimateVersionKind)}>
            {(Object.keys(ESTIMATE_VERSION_KINDS) as EstimateVersionKind[]).map((k) => (
              <option key={k} value={k}>
                {ESTIMATE_VERSION_KINDS[k]}
              </option>
            ))}
          </Select>
        </Field>
        {rootId ? (
          <>
            {linked.data?.changes.length ? (
              <Field label="Запрос на изменение">
                <Select value={changeId} onChange={(e) => setChangeId(e.target.value)}>
                  <option value="">Без запроса</option>
                  {linked.data.changes.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.number} {c.title}
                    </option>
                  ))}
                </Select>
              </Field>
            ) : null}
            <Field label="Причина новой версии">
              <Input
                value={reason}
                maxLength={500}
                placeholder="Ревизия ПСД, замечание экспертизы, допработы, новые цены"
                onChange={(e) => setReason(e.target.value)}
              />
            </Field>
          </>
        ) : null}
        <ErrorBox error={m.error} />
      </div>
    </Dialog>
  );
}

/** Итог импорта: суммы и расхождения */
export function ImportResultDialog({ r, onClose }: { r: EstimateImportResult; onClose: () => void }) {
  return (
    <Dialog
      open
      onOpenChange={(v) => !v && onClose()}
      title={`Смета ${r.code} загружена`}
      description={r.name}
      footer={
        <Button className="min-h-[44px] md:min-h-0" variant="primary" onClick={onClose}>
          Открыть смету
        </Button>
      }
    >
      <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-[14px]">
        <dt className="text-ink-2">Позиций</dt>
        <dd className="num text-right">{r.items}</dd>
        <dt className="text-ink-2">Всего по смете</dt>
        <dd className="num text-right">{tenge(r.totals.amount)}</dd>
        <dt className="text-ink-2">Трудоёмкость</dt>
        <dd className="num text-right">{hours(r.totals.laborHours)} чел.-ч</dd>
        <dt className="text-ink-2">В нормативную базу добавлено</dt>
        <dd className="num text-right">
          {r.normsAdded} норм, {r.resourcesAdded} ресурсов
        </dd>
      </dl>
      {r.issues.length ? (
        <div className="mt-4 rounded-md bg-warning-soft p-3 text-[13px] text-warning">
          <p className="font-medium">Пересчёт расходится с файлом:</p>
          <ul className="mt-1 list-disc pl-5">
            {r.issues.slice(0, 12).map((s) => (
              <li key={s}>{s}</li>
            ))}
            {r.issues.length > 12 ? <li>… и ещё {r.issues.length - 12}</li> : null}
          </ul>
        </div>
      ) : (
        <p className="mt-4 text-[13px] text-success">Пересчёт сошёлся с файлом по всем позициям и итогам.</p>
      )}
    </Dialog>
  );
}

function ProjectPicker({
  projects,
  value,
  onChange,
}: {
  projects: ProjectSummary[];
  value: string;
  onChange: (id: string) => void;
}) {
  return (
    <Select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      aria-label="Проект"
      className="max-w-full md:w-[360px]"
    >
      <option value="">— выберите проект —</option>
      {projects.map((p) => (
        <option key={p.id} value={p.id}>
          {p.code} — {p.name}
        </option>
      ))}
    </Select>
  );
}

export function EstimatesPage() {
  const me = useMe();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const search = useSearch({ strict: false }) as { projectId?: string };
  const projects = useQuery(projectsQuery('all'));
  const [projectId, setProjectId] = useState(search.projectId ?? '');
  const [all, setAll] = useState(false);
  const [importing, setImporting] = useState(false);
  const [done, setDone] = useState<EstimateImportResult | null>(null);
  useCrumbs([]);

  useEffect(() => {
    if (!projectId && projects.data?.length === 1) setProjectId(projects.data[0]!.id);
  }, [projects.data, projectId]);
  // Переход на /estimates?projectId=… с уже открытой страницы: проект берётся из адреса
  useEffect(() => {
    if (search.projectId) setProjectId(search.projectId);
  }, [search.projectId]);
  useEffect(() => {
    if (projectId) {
      try {
        localStorage.setItem('epc:estimates:project', projectId);
      } catch {}
    }
  }, [projectId]);
  useEffect(() => {
    if (!projectId && !search.projectId) {
      try {
        const saved = localStorage.getItem('epc:estimates:project');
        if (saved) setProjectId(saved);
      } catch {}
    }
  }, [projectId, search.projectId]);

  const project = projects.data?.find((p) => p.id === projectId);
  const list = useQuery(estimatesQuery({ projectId: projectId || undefined, all }));
  const summary = useQuery({ ...estimatesSummaryQuery(projectId), enabled: Boolean(projectId) });
  const money = seesMoney(me.access, 'acts_invoices');
  const canEdit = project
    ? can(me.access, 'estimate.edit', {
        legalEntityId: project.legalEntityId,
        directionId: project.directionId,
        projectId: project.id,
        projectPath: [project.parentId],
      })
    : false;

  const onDone = (r: EstimateImportResult) => {
    setImporting(false);
    setDone(r);
    qc.invalidateQueries({ queryKey: ['estimates'] });
    qc.invalidateQueries({ queryKey: ['norms'] });
    toast(`Смета ${r.code} загружена`);
  };

  const rows = list.data ?? [];
  const byObject = new Map<string, EstimateSummary[]>();
  for (const e of rows) {
    const k = e.objectName ?? 'Без объекта';
    byObject.set(k, [...(byObject.get(k) ?? []), e]);
  }

  return (
    <Page>
      <PageHeader
        title="Сметы"
        subtitle="Локальные и объектные сметы на нормативной базе РК, версии, ресурсная ведомость"
        actions={
          project ? (
            <>
              <Button
                className="min-h-[44px] md:min-h-0"
                variant="secondary"
                icon={<ListTree className="size-4" />}
                onClick={() => navigate({ to: '/estimates/resources', search: { projectId: project.id } })}
              >
                Ресурсная ведомость
              </Button>
              {canEdit ? (
                <Button
                  className="min-h-[44px] md:min-h-0"
                  variant="primary"
                  icon={<FileUp className="size-4" />}
                  onClick={() => setImporting(true)}
                >
                  Загрузить KENML или Excel
                </Button>
              ) : null}
            </>
          ) : null
        }
      >
        <div className="mt-3 flex flex-wrap items-center gap-3">
          {projects.isPending ? (
            <Spinner />
          ) : (
            <ProjectPicker projects={projects.data ?? []} value={projectId} onChange={setProjectId} />
          )}
          {projectId ? (
            <Checkbox label="Все версии" checked={all} onChange={(e) => setAll(e.target.checked)} />
          ) : null}
        </div>
      </PageHeader>

      {!projectId ? (
        <EmptyState title="Выберите проект">
          Сметы ведутся по проектам: выберите проект, чтобы увидеть его сметы или загрузить новую.
        </EmptyState>
      ) : list.isPending ? (
        <Spinner />
      ) : list.isError ? (
        <ErrorBox error={list.error} />
      ) : !rows.length ? (
        <EmptyState
          title="Смет пока нет"
          action={
            canEdit ? (
              <Button
                className="min-h-[44px] md:min-h-0"
                variant="primary"
                icon={<FileUp className="size-4" />}
                onClick={() => setImporting(true)}
              >
                Загрузить KENML или Excel
              </Button>
            ) : null
          }
        >
          Загрузите сметы из АВС-KZ, Сметы РК или SANA в формате KENML 1.0 — каждая станет локальной сметой
          проекта с позициями, ресурсами и коэффициентами.
        </EmptyState>
      ) : (
        <div className="flex flex-col gap-6">
          {summary.data ? (
            <TitleBlock
              items={[
                { label: 'Смет (текущие версии)', value: summary.data.count },
                {
                  label: 'Сметная стоимость',
                  value: money ? tengeShort(summary.data.amount) : '—',
                  note: money ? tenge(summary.data.amount) : 'суммы скрыты',
                },
                {
                  label: 'Не привязано к бюджету',
                  value: money ? tengeShort(summary.data.unlinkedAmount) : '—',
                  note: 'разделы бюджета задаются в карточке сметы',
                  tone: money && summary.data.unlinkedAmount ? 'danger' : 'default',
                },
                { label: 'Объектов со сметами', value: summary.data.byObject.length },
              ]}
            />
          ) : null}
          {[...byObject.entries()].map(([objectName, items]) => (
            <Panel
              key={objectName}
              title={objectName}
              meta={`${items.length} ${items.length === 1 ? 'смета' : 'смет'}`}
              bodyClassName="p-0"
            >
              <ul className="divide-y divide-line">
                {items.map((e) => (
                  <li key={e.id}>
                    <Link
                      to="/estimates/$estimateId"
                      params={{ estimateId: e.id }}
                      className="flex min-h-[44px] flex-col gap-1 px-5 py-3 hover:bg-surface-2 md:flex-row md:items-center md:gap-4"
                    >
                      <span className="num w-[110px] shrink-0 text-[13px] text-ink-2">{e.code}</span>
                      <span className="min-w-0 flex-1 text-[14px] font-medium text-ink [overflow-wrap:anywhere]">
                        {e.name}
                      </span>
                      <span className="flex flex-wrap items-center gap-2 text-[12px] text-ink-2">
                        <span>
                          v{e.versionNo} · {ESTIMATE_VERSION_KINDS[e.versionKind]}
                        </span>
                        {!e.isCurrent ? <Badge>прежняя версия</Badge> : null}
                        {e.hasIssues ? <Badge tone="warning">расхождения</Badge> : null}
                        <StatusBadge status={e.status} />
                      </span>
                      <span
                        className={clsx(
                          'num shrink-0 text-right text-[14px] md:w-[160px]',
                          money ? 'text-ink' : 'text-ink-2',
                        )}
                      >
                        {money ? tenge(e.totals.amount) : 'суммы скрыты'}
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>
            </Panel>
          ))}
        </div>
      )}
      {importing && project ? (
        <ImportDialog project={project} onClose={() => setImporting(false)} onDone={onDone} />
      ) : null}
      {done ? (
        <ImportResultDialog
          r={done}
          onClose={() => {
            const id = done.estimateId;
            setDone(null);
            navigate({ to: '/estimates/$estimateId', params: { estimateId: id } });
          }}
        />
      ) : null}
    </Page>
  );
}
